import { useState } from "react";
import { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Download, FileText, DoorOpen, Receipt } from "lucide-react";

function ReportCard({ title, description, testid, endpoint, hasDates, icon }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (hasDates) { if (start) params.set("start", start); if (end) params.set("end", end); }
      const url = `${BACKEND_URL}/api/admin/reports/${endpoint}?${params.toString()}`;
      const resp = await fetch(url, { credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("baraka_token") || ""}` } });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${endpoint.replace(".csv", "")}-${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Report downloaded");
    } catch (e) { toast.error(e.message); }
    finally { setDownloading(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2 font-heading">{icon} {title}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-[color:var(--muted-text)]">{description}</p>
        {hasDates && (
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div><Label>Start</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid={`${testid}-start`} /></div>
            <div><Label>End</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid={`${testid}-end`} /></div>
          </div>
        )}
        <Button onClick={download} disabled={downloading} data-testid={testid} className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">
          <Download className="w-4 h-4 mr-1" /> {downloading ? "Preparing…" : "Download CSV"}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl text-[color:var(--deep)]">Reports</h1>
        <p className="text-sm text-[color:var(--muted-text)] mt-1">Download hotel data for accounting, tax and audits.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <ReportCard
          title="Bookings"
          description="Reference, guest, room type, room #, dates, amount, payment & status."
          testid="download-bookings-btn"
          endpoint="bookings.csv"
          hasDates
          icon={<FileText className="w-5 h-5 text-[color:var(--brand)]" />}
        />
        <ReportCard
          title="Expenses"
          description="All hotel expenses with category, vendor, amount and paid status."
          testid="download-expenses-btn"
          endpoint="expenses.csv"
          hasDates
          icon={<Receipt className="w-5 h-5 text-[color:var(--brand)]" />}
        />
        <ReportCard
          title="Occupancy snapshot"
          description="Per room-type: total units, currently occupied, available now."
          testid="download-occupancy-btn"
          endpoint="occupancy.csv"
          hasDates={false}
          icon={<DoorOpen className="w-5 h-5 text-[color:var(--brand)]" />}
        />
      </div>
    </div>
  );
}
